import * as THREE from 'three';
import { GestureManager, GestureType } from './GestureManager';
import { FloatingDroid, LightningEffect, ExplosionEffect } from './GameEntities';

export class ForceTrainerGame {
    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private canvas!: HTMLCanvasElement;
    
    private gestureManager!: GestureManager;
    private videoElement!: HTMLVideoElement;
    
    private objects: FloatingDroid[] = [];
    private effects: (LightningEffect | ExplosionEffect)[] = [];
    
    private isRunning = true;
    private animationId: number = 0;
    private clock!: THREE.Clock;
    
    // Game State
    private score: number = 0;
    private scoreElement: HTMLElement | null = null;
    private debugText: HTMLElement | null = null;
    
    // Reticle
    private reticle: HTMLElement | null = null;
    private raycaster: THREE.Raycaster = new THREE.Raycaster();
    
    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas;
        this.init();
    }
    
    private async init() {
        console.log("ForceTrainer: Initializing...");
        
        // Setup Scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x050510);
        this.scene.fog = new THREE.Fog(0x050510, 10, 50);
        
        // Setup Camera
        this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 100);
        this.camera.position.set(0, 1.6, 5); // Stand back a bit
        
        // Setup Renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: false
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        
        // Lights
        const ambientLight = new THREE.AmbientLight(0x404040, 2);
        this.scene.add(ambientLight);
        
        const dirLight = new THREE.DirectionalLight(0xffffff, 2);
        dirLight.position.set(5, 10, 5);
        dirLight.castShadow = true;
        this.scene.add(dirLight);
        
        // Floor (Death Star style grid)
        const grid = new THREE.GridHelper(50, 50, 0x444444, 0x111111);
        this.scene.add(grid);
        
        // Gesture Manager
        this.gestureManager = new GestureManager();
        
        // Camera Feed
        await this.setupCamera();
        
        // UI
        this.createUI();
        
        // Initial Objects
        this.clock = new THREE.Clock();
        this.spawnDroid();
        
        // Start Loop
        this.animate();
        
        // Handle Resize
        window.addEventListener('resize', this.onResize.bind(this));
    }
    
    private async setupCamera() {
        this.videoElement = document.createElement('video');
        this.videoElement.style.display = 'none'; // process in background
        this.videoElement.autoplay = true;
        this.videoElement.playsInline = true;
        document.body.appendChild(this.videoElement);
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            this.videoElement.srcObject = stream;
            await new Promise((resolve) => {
                this.videoElement.onloadedmetadata = () => {
                   this.videoElement.play(); 
                   resolve(true);
                };
            });
            console.log("ForceTrainer: Camera ready.");
        } catch (e) {
            console.error("ForceTrainer: Camera access denied", e);
            alert("Camera access is required for Force Trainer!");
        }
    }
    
    private createUI() {
        const uiContainer = document.createElement('div');
        uiContainer.id = 'game-ui';
        uiContainer.style.position = 'absolute';
        uiContainer.style.top = '0';
        uiContainer.style.left = '0';
        uiContainer.style.width = '100%';
        uiContainer.style.height = '100%';
        uiContainer.style.pointerEvents = 'none';
        
        // Score
        this.scoreElement = document.createElement('div');
        this.scoreElement.style.position = 'absolute';
        this.scoreElement.style.top = '20px';
        this.scoreElement.style.left = '20px';
        this.scoreElement.style.fontSize = '24px';
        this.scoreElement.style.color = '#00d4ff';
        this.scoreElement.style.fontFamily = "'Orbitron', sans-serif";
        this.scoreElement.innerHTML = 'SCORE: 0';
        uiContainer.appendChild(this.scoreElement);
        
        // Debug
        this.debugText = document.createElement('div');
        this.debugText.style.position = 'absolute';
        this.debugText.style.top = '60px';
        this.debugText.style.left = '20px';
        this.debugText.style.fontSize = '14px';
        this.debugText.style.color = '#00d4ff';
        this.debugText.style.fontFamily = "'Orbitron', sans-serif";
        this.debugText.style.opacity = '0.7';
        this.debugText.innerHTML = 'Status: Initializing...';
        uiContainer.appendChild(this.debugText);
        
        // Instructions
        const instructions = document.createElement('div');
        instructions.style.position = 'absolute';
        instructions.style.top = '100px';
        instructions.style.left = '20px';
        instructions.style.fontSize = '12px';
        instructions.style.color = '#00d4ff';
        instructions.style.fontFamily = "'Orbitron', sans-serif";
        instructions.innerHTML = `
            CONTROLS:<br>
            ✋ LEFT HAND: Aim Reticle<br>
            ✋ RIGHT PALM: Force Push<br>
            ✊ RIGHT FIST: Force Grip<br>
            ☝️ RIGHT POINT: Lightning
        `;
        uiContainer.appendChild(instructions);
        
        // Reticle
        this.reticle = document.createElement('div');
        this.reticle.style.position = 'absolute';
        this.reticle.style.width = '40px';
        this.reticle.style.height = '40px';
        this.reticle.style.border = '2px solid rgba(0, 212, 255, 0.8)';
        this.reticle.style.borderRadius = '50%';
        this.reticle.style.transform = 'translate(-50%, -50%)';
        this.reticle.style.boxShadow = '0 0 10px #00d4ff';
        this.reticle.style.transition = 'width 0.1s, height 0.1s';
        this.reticle.style.display = 'none'; // Hidden until hand is found
        uiContainer.appendChild(this.reticle);
        
        document.body.appendChild(uiContainer);
    }
    
    private spawnDroid() {
        if (this.objects.length > 5) return; // limit count
        
        const droid = new FloatingDroid();
        // Spawn randomly in front
        droid.mesh.position.set(
            (Math.random() - 0.5) * 6,
            1 + Math.random() * 2,
            -5 - Math.random() * 5
        );
        this.scene.add(droid.mesh);
        this.objects.push(droid);
    }
    
    private onResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
    
    private updateReticle(handPos: { x: number, y: number } | null, isLocked: boolean) {
        if (!this.reticle) return;
        
        if (handPos) {
             this.reticle.style.display = 'block';
             // Map 0-1 to screen pixels
             // Note: MediaPipe x is 0-1 (left-right), y is 0-1 (top-bottom)
             this.reticle.style.left = `${handPos.x * window.innerWidth}px`;
             this.reticle.style.top = `${handPos.y * window.innerHeight}px`;
             
             if (isLocked) {
                 this.reticle.style.borderColor = '#ff0000';
                 this.reticle.style.width = '30px';
                 this.reticle.style.height = '30px';
             } else {
                 this.reticle.style.borderColor = '#00d4ff';
                 this.reticle.style.width = '40px';
                 this.reticle.style.height = '40px';
             }
        } else {
             this.reticle.style.display = 'none';
        }
    }
    
    private animate() {
        if (!this.isRunning) return;
        
        this.animationId = requestAnimationFrame(this.animate.bind(this));
        
        const deltaTime = this.clock.getDelta();
        
        // 1. Gesture Detection
        let rightGesture: GestureType = 'None';
        let leftHandPos: { x: number, y: number } | null = null;
        let targetedConfig: FloatingDroid | null = null;
        
        if (this.gestureManager.isReady()) {
            const result = this.gestureManager.detect(this.videoElement, Date.now());
            
            // Left Hand: AIMING (Position)
            const leftData = this.gestureManager.getHandData(result, 'Left');
            if (leftData) {
                leftHandPos = { x: leftData.x, y: leftData.y };
            }
            
            // Right Hand: ACTION (Gesture)
            const rightData = this.gestureManager.getHandData(result, 'Right');
            if (rightData) {
                rightGesture = rightData.gesture;
            }
            
            if (this.debugText) {
                this.debugText.innerHTML = `L: ${leftData ? 'Active' : '-'} | R: ${rightGesture}`;
            }
            
            // Aiming Logic (Using LEFT HAND)
            if (leftHandPos) {
                 // Convert handPos to normalized device coordinates (-1 to +1)
                 // MediaPipe: x: 0 (left) -> 1 (right), y: 0 (top) -> 1 (bottom)
                 // Three.js NDC: x: -1 (left) -> 1 (right), y: 1 (top) -> -1 (bottom)
                 const ndc = new THREE.Vector2(
                     (leftHandPos.x * 2) - 1,
                     -(leftHandPos.y * 2) + 1
                 );
                 
                 this.raycaster.setFromCamera(ndc, this.camera);
                 
                 // Intersect with valid targets (children of objects)
                 const meshes = this.objects.map(o => o.mesh);
                 const intersects = this.raycaster.intersectObjects(meshes, true);
                 
                 if (intersects.length > 0) {
                     // Find the root object
                     let current: THREE.Object3D | null = intersects[0].object;
                     while(current) {
                         if (current.userData && current.userData.entity) {
                             targetedConfig = current.userData.entity as FloatingDroid;
                             break;
                         }
                         current = current.parent;
                     }
                 }
            }
        }
        
        // Update Reticle UI (Tracks LEFT HAND)
        this.updateReticle(leftHandPos, !!targetedConfig);
        
        // 2. Game Logic
        
        // Spawn logic
        if (Math.random() < 0.01) this.spawnDroid();
        
        // Interaction (Using RIGHT HAND Gesture)
        if (rightGesture === 'Open_Palm') {
            // PUSH AWAY (All or targeted?) - Let's keep it Area of Effect for "Force Push" feel
            this.objects.forEach(obj => {
                const dir = obj.mesh.position.clone().sub(this.camera.position).normalize();
                obj.applyForce(dir.multiplyScalar(20 * deltaTime));
            });
        }
        else if (rightGesture === 'Closed_Fist') {
             // PULL / HOLD
             if (targetedConfig) {
                 // Specific grip on target
                 const targetPos = new THREE.Vector3(0, 1.5, -3);
                 const dir = targetPos.sub(targetedConfig.mesh.position).normalize();
                 targetedConfig.applyForce(dir.multiplyScalar(50 * deltaTime));
             } else {
                 // Weak general pull
                 this.objects.forEach(obj => {
                     const target = new THREE.Vector3(0, 1.5, -3); // Hold point
                     const dir = target.sub(obj.mesh.position).normalize();
                     obj.applyForce(dir.multiplyScalar(30 * deltaTime));
                 });
             }
        }
        else if (rightGesture === 'Pointing_Up' && leftHandPos && targetedConfig) {
            // LIGHTNING at TARGET
             // Create lightning from RIGHT HAND position (if available) or generic right side
             // If we have right hand pos, use it. But we only extracted gesture. Let's extract pos too if needed.
             // For simplicity, let's assume Right Hand is somewhere on the right.
             
             let start = new THREE.Vector3(1, -1, 1).unproject(this.camera);
             
             // Try to get actual right hand pos if possible, but we didn't save it in var. 
             // Accessing again via manager is cheap? Or just assume offset.
             // Manager result is shared? We called detect once.

             
             // Let's just use Screen Right Bottom
             const screenRight = new THREE.Vector2(0.8, -0.8);
             start = new THREE.Vector3(screenRight.x, screenRight.y, 0.5).unproject(this.camera);

             // Create lightning effect
             const lightning = new LightningEffect(start, targetedConfig.mesh.position);
             this.scene.add(lightning.mesh);
             this.effects.push(lightning);
             
             // DESTROY
             targetedConfig.destroy();
             
             // Explosion
             const explosion = new ExplosionEffect(targetedConfig.mesh.position);
             this.scene.add(explosion.mesh);
             this.effects.push(explosion);
             
             // Score
             this.score += 100;
             if (this.scoreElement) this.scoreElement.innerHTML = `SCORE: ${this.score}`;
        }
        
        // Update Objects
        for (let i = this.objects.length - 1; i >= 0; i--) {
            const obj = this.objects[i];
            obj.update(deltaTime);
            
            if (obj.isDestroyed || obj.mesh.position.z < -20 || obj.mesh.position.y < -10 || obj.mesh.position.y > 10) {
                 if (!obj.isDestroyed) {
                     // Clean removal if just drifted away
                     this.scene.remove(obj.mesh);
                     obj.dispose();
                 } else {
                     // It was destroyed by player, removing mesh was handled in destroy() but we must ensure logic
                     this.scene.remove(obj.mesh);
                     obj.dispose();
                 }
                 this.objects.splice(i, 1);
            }
        }
        
        // Update Effects
        for (let i = this.effects.length - 1; i >= 0; i--) {
            const eff = this.effects[i];
            eff.update(deltaTime);
            if ((eff as any).isDead()) {
                 this.scene.remove(eff.mesh);
                 eff.dispose();
                 this.effects.splice(i, 1);
            }
        }
        
        this.renderer.render(this.scene, this.camera);
    }
    
    public dispose() {
        this.isRunning = false;
        cancelAnimationFrame(this.animationId);
        
        // Clean scene
        this.objects.forEach(o => o.dispose());
        this.effects.forEach(e => e.dispose());
        this.scene.clear();
        this.renderer.dispose();
        
        // Clean UI
        const ui = document.getElementById('game-ui');
        if (ui) ui.remove();
        
        // Clean Video
        if (this.videoElement) {
             const stream = this.videoElement.srcObject as MediaStream;
             if (stream) stream.getTracks().forEach(track => track.stop());
             this.videoElement.remove();
        }
        
        window.removeEventListener('resize', this.onResize.bind(this));
    }
}
